import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Award, TrendingUp } from 'lucide-react';
import Select from 'react-select';
import { formatearFechaCompleta, formatearPrecio} from '../../../../utils/utils';
import { useTutoresModel } from '../../../../modelos/Tutores/modeloTutores';
import EditableField from '../../../../components/EditableFields';
import { useDetalleSolicitudModel } from '../../../../modelos/Solicitudes/modeloSolicitud';
import { useDialog } from '../../../../components/Dialogs/Dialogprovider';
import CotizarPorTutor from './CotizarPorTutor';

//card para cotizaciones
export const TutorStats = ({ stats, loading }) => {
  // Muestra un indicador de carga mientras se obtienen los datos.
  if (loading) {
    return <td className="p-3 text-xs text-gray-500 animate-pulse">Cargando stats...</td>;
  }
  // No muestra nada si la carga terminó y no hay datos para ese tutor.
  if (!stats) {
    return <td className="p-3 text-xs text-gray-400">--</td>;
  }

  return (
    <td className="p-3">
      {/* - flex-col: Apila los elementos verticalmente.
        - items-start: Alinea los elementos a la izquierda.
        - space-y-1: Añade un pequeño espacio vertical entre cada estadística.
      */}
      <div className="flex flex-col items-start space-y-1 text-xs text-gray-600">
        
        <div className="flex items-center" title="Servicios Totales Realizados">
          <Award size={12} className="text-blue-500 mr-1.5" />
          <span>
            <span className="font-semibold">{stats.totalServicios || 0}</span> servicios
          </span>
        </div>
        
        <div className="flex items-center" title="Tasa de Agendado sobre Cotizadas">
          <TrendingUp size={12} className="text-green-500 mr-1.5" />
          <span>
            <span className="font-semibold">{stats.percentAgendadoReal || '0.00'}%</span> agendado
          </span>
        </div>

        <div className="flex items-center" title="Ganancia Promedio por Servicio">
          <span className="font-semibold mr-1 text-yellow-600">$</span>
          <span>
            Prom. <span className="font-semibold">{formatearPrecio(stats.gananciaPromedio || 0, 0)}</span>
          </span>
        </div>

      </div>
    </td>
  );
};
//card para otra cosa

const Cotizaciones = ({ 
  solicitud, 
  deleteCotizacionLocal,
  addNewCotizacion,
  onAgendarTrabajo 
}) => {
  // Estados SOLO para el modal de cotizar por tutor
  const [showModalCotizar, setShowModalCotizar] = useState(false);
  const [tutorSeleccionado, setTutorSeleccionado] = useState(null);
  const [precioCotizacion, setPrecioCotizacion] = useState('');
  const [comentarioCotizacion, setComentarioCotizacion] = useState('');
  const [loadingCotizar, setLoadingCotizar] = useState(false);
  
  const {tutoresOptions, cargarTutores, obtenerEstadisticasTutor} = useTutoresModel();

  const {
    actualizarSolicitud,
  } = useDetalleSolicitudModel();

  //estadisticas
  const [estadisticasTutores, setEstadisticasTutores] = useState({});
  const [loadingEstadisticas, setLoadingEstadisticas] = useState(false);

  //dialogs
  const dialogs = useDialog();

  // ✅ FUNCIÓN SIMPLIFICADA - Solo llama al callback del padre
  const agendarTrabajo = (cotizacion) => {

    const ejecutarAccion = () => {
      onAgendarTrabajo?.(cotizacion);
    };

    if (solicitud.fechaentrega) {
      const fechaNueva = new Date(solicitud.fechaentrega);
      const fechaActual = new Date();

      // 2. Verificamos si la fecha es realmente en el pasado
      // Comparamos los objetos Date directamente
      if (fechaNueva < fechaActual) {
        dialogs.showConfirm(
          `Estás intentado agendar un trabajo con fecha pasada (${formatearFechaCompleta(fechaNueva)}). ¿Estás seguro de que deseas continuar?`,
          () => {
            ejecutarAccion();
          },
          'Advertencia de fecha'
        );
        return; // Salimos para esperar la respuesta del usuario en el dialog
      }
    }

    ejecutarAccion();
  };

  // Filtrar solo cotizaciones válidas (mayores a 0)
  const cotizacionesValidas = solicitud?.cotizaciones?.filter(
    cotizacion => cotizacion.cotizacion > 0
  ) || [];

  //obtener estadisticas
  useEffect(() => {
    const cargarEstadisticas = async () => {
      // Si no hay cotizaciones válidas, no hacemos nada.
      if (cotizacionesValidas.length === 0) {
        setEstadisticasTutores({});
        return;
      }
      
      setLoadingEstadisticas(true);
      try {
        // 1. Obtenemos una lista de tutores únicos para no hacer llamadas duplicadas.
        const tutoresUnicos = [...new Set(cotizacionesValidas.map(c => c.nombretutor))];

        // 2. Creamos un array de promesas, una por cada tutor.
        const promesas = tutoresUnicos.map(async (nombreTutor) => {
          const stats = await obtenerEstadisticasTutor(nombreTutor);
          // Devolvemos el nombre y sus stats para poder asociarlos después.
          return { nombreTutor, stats };
        });

        // 3. Ejecutamos todas las llamadas a la API en paralelo.
        const resultados = await Promise.all(promesas);

        // 4. Convertimos el array de resultados en un objeto para un acceso más fácil.
        const statsMap = resultados.reduce((acc, { nombreTutor, stats }) => {
          if (stats) { // Solo añadimos si la llamada fue exitosa.
            acc[nombreTutor] = stats;
          }
          return acc;
        }, {});

        setEstadisticasTutores(statsMap);

      } catch (error) {
        console.error("Error al cargar el conjunto de estadísticas de tutores:", error);
      } finally {
        setLoadingEstadisticas(false);
      }
    };

    cargarEstadisticas();
  }, []);

  //cotizaciones no validas
  const cotizacionesRechazadas = solicitud?.cotizaciones?.filter(
    cotizacion => cotizacion.cotizacion <= 0
  ) || [solicitud];

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    if (cotizacionesValidas.length === 0) {
      return {
        mejorCotizacion: null,
        promedio: 0,
        total: 0
      };
    }

    // Encontrar la mejor cotización (precio más bajo)
    const mejorCotizacion = cotizacionesValidas.reduce((mejor, actual) => {
      return actual.cotizacion < mejor.cotizacion ? actual : mejor;
    });

    // Calcular promedio
    const sumaPrecios = cotizacionesValidas.reduce(
      (suma, cotizacion) => suma + cotizacion.cotizacion, 0
    );
    const promedio = sumaPrecios / cotizacionesValidas.length;

    return {
      mejorCotizacion,
      promedio,
      total: cotizacionesValidas.length
    };
  };

  const { mejorCotizacion, promedio, total } = calcularEstadisticas();

  // Función para abrir modal de cotizar
  const abrirModalCotizar = () => {
    setShowModalCotizar(true);
    setTutorSeleccionado(null);
    setPrecioCotizacion('');
    setComentarioCotizacion('');
  };

  // Función para cerrar modal de cotizar
  const cerrarModalCotizar = () => {
    setShowModalCotizar(false);
    setTutorSeleccionado(null);
    setPrecioCotizacion('');
    setComentarioCotizacion('');
  };

  // Función para guardar cotización por tutor
  const handleCotizarPorTutor = async () => {
    if (!tutorSeleccionado || !precioCotizacion) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }

    setLoadingCotizar(true);
    try {
      console.log("Cotización por tutor:", {
        empresa: solicitud.nombreEmpresa,
        precio: precioCotizacion,
        comentario: comentarioCotizacion,
        solicitudId: solicitud.id,
        tutor: tutorSeleccionado.value
      });

      await addNewCotizacion(
        solicitud.nombreEmpresa,
        precioCotizacion,
        comentarioCotizacion,
        solicitud.id,
        tutorSeleccionado.value
      );

      // Cerrar modal y limpiar campos
      cerrarModalCotizar();
      
    } catch (error) {
      console.error('Error al crear cotización:', error);
      alert('Error al crear la cotización');
    } finally {
      setLoadingCotizar(false);
    }
  };

  // Función para manejar la aprobación de la mejor cotización
  const aprobarMejorCotizacion = () => {
    if (mejorCotizacion) {
      agendarTrabajo(mejorCotizacion);
    }
  };

  const handleSave = async(cotizacionId,field,value) =>{
    const finalupdate = {
        "value" : value,
        "elementId": cotizacionId,
      }
      await actualizarSolicitud(solicitud.id,solicitud.nombreEmpresa,field,finalupdate,'array');
  }
  
  return (
    <div className="space-y-6">

      {/* Disclaimer y estadísticas */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">
              Nota Importante sobre la Evaluación
            </h3>
            <div className="mt-2 text-sm text-amber-700">
              <p>
                Actualmente, la "mejor cotización" se determina únicamente por el precio más bajo. 
                Este criterio será mejorado en futuras versiones para incluir factores como experiencia del tutor, 
                calificaciones, tiempo de entrega y calidad del trabajo.
              </p>
            </div>
             <CotizarPorTutor 
              solicitud={solicitud} 
              addNewCotizacion={addNewCotizacion} 
            />
          </div>
        </div>
      </div>

      {/* Estadísticas de cotizaciones */}
      {total > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Mejor cotización */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-green-600">Mejor Cotización</p>
                <p className="text-2xl font-bold text-green-900">
                  {mejorCotizacion ? formatearPrecio(mejorCotizacion.cotizacion) : 'N/A'}
                </p>
                <p className="text-sm text-green-700">
                  {mejorCotizacion ? `por ${mejorCotizacion.nombretutor}` : 'No disponible'}
                </p>
              </div>
            </div>
          </div>

          {/* Promedio */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-blue-600">Precio Promedio</p>
                <p className="text-2xl font-bold text-blue-900">
                  {formatearPrecio(Math.round(promedio))}
                </p>
                <p className="text-sm text-blue-700">
                  de {total} cotización{total !== 1 ? 'es' : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Total de cotizaciones */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Recibidas</p>
                <p className="text-2xl font-bold text-gray-900">{total}</p>
                <p className="text-sm text-gray-700">
                  cotización{total !== 1 ? 'es' : ''} válida{total !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No hay cotizaciones aún</h3>
          <p className="mt-1 text-sm text-gray-500">Los tutores comenzarán a cotizar pronto.</p>
        </div>
      )}

      {/* Tabla de cotizaciones */}
      {total > 0 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Detalle de Cotizaciones Recibidas</h3>
            <div className="flex gap-2">
              
              <button 
                onClick={aprobarMejorCotizacion}
                disabled={!mejorCotizacion}
                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle size={16} className="mr-2" />
                Aprobar Mejor Cotización
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-sm border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tutor</th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio</th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comentario</th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                  <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stats</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {cotizacionesValidas.map((cotizacion) => {
                  const esMejorCotizacion = mejorCotizacion && cotizacion.id === mejorCotizacion.id;
                  const diferenciaDePrecio = mejorCotizacion ? cotizacion.cotizacion - mejorCotizacion.cotizacion : 0;
                  const statsDelTutor = estadisticasTutores[cotizacion.nombretutor];

                  return (
                    <tr 
                      key={cotizacion.id} 
                      className={`hover:bg-gray-50 ${
                        esMejorCotizacion ? 'bg-green-50 border-l-4 border-green-400' : ''
                      }`}
                    >
                      <td className="p-3">
                        <div className="flex items-center">
                          <span className="font-medium">{cotizacion.nombretutor}</span>
                          {/* Es mejor precio 
                          {esMejorCotizacion && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Mejor precio
                            </span>
                          )}
                            */}
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col">
                          <span className={`font-semibold ${
                            esMejorCotizacion ? 'text-green-700' : 'text-green-600'
                          }`}>
                             <EditableField
                                field="cotizaciones.cotizacion"
                                label="Precio"
                                value={cotizacion.cotizacion}
                                type="number"
                                isAdmin={true}
                                onSave={(field, value) => {
                                    handleSave(cotizacion.nombretutor,field,value);
                                }}
                                
                                className="w-full bg-transparent border-none p-0 focus:outline-none focus:ring-0"
                            />
                          </span>
                          
                          {!esMejorCotizacion && diferenciaDePrecio > 0 && (
                            <span className="text-xs text-red-600">
                              +{formatearPrecio(diferenciaDePrecio)} más que la mejor
                            </span>
                          )}
                          
                          {promedio > 0 && Math.abs(cotizacion.cotizacion - promedio) > 1 && (
                            <span className={`text-xs ${
                              cotizacion.cotizacion < promedio ? 'text-green-600' : 'text-orange-600'
                            }`}>
                              {cotizacion.cotizacion < promedio ? '-' : '+'}
                              {formatearPrecio(Math.abs(cotizacion.cotizacion - promedio))} del promedio
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {formatearFechaCompleta(cotizacion.fechaconfirmacion)}
                      </td>
                      <td className="p-3">
                        <div className="max-w-xs">
                          <p className="text-sm text-gray-900 truncate" title={cotizacion.comentariocotizacion}>
                            <EditableField
                              field="cotizaciones.comentariocotizacion"
                              label="Comentario de cotizacion"
                              value={cotizacion.comentariocotizacion || 'Sin comentarios'}
                              type="text"
                              isAdmin={true}
                              onSave={(field,value) =>{
                                 handleSave(cotizacion.nombretutor,field,value);                     
                              }}
                            />
                          </p>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button 
                            onClick={() => agendarTrabajo(cotizacion)}
                            className="text-green-600 hover:bg-green-100 p-2 rounded-full transition-colors"
                            title="Aprobar cotización"
                          >
                            <CheckCircle size={16} />
                          </button>

                          <button 
                            onClick={() => deleteCotizacionLocal(cotizacion)} 
                            className="text-red-600 hover:bg-red-100 p-2 rounded-full transition-colors"
                            title="Rechazar cotización"
                          >
                            <XCircle size={16} />
                          </button>
                        </div>
                      </td>

                      <TutorStats stats={statsDelTutor} loading={loadingEstadisticas} />

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Análisis adicional */}
      {total > 1 && mejorCotizacion && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2">Análisis Rápido</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Diferencia mayor-menor:</span>
              <span className="ml-2 font-medium">
                {formatearPrecio(
                  Math.max(...cotizacionesValidas.map(c => c.cotizacion)) - 
                  Math.min(...cotizacionesValidas.map(c => c.cotizacion))
                )}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Ahorro vs promedio:</span>
              <span className="ml-2 font-medium text-green-600">
                {formatearPrecio(Math.round(promedio) - mejorCotizacion.cotizacion)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* rechazos */}
      {cotizacionesRechazadas.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Tutores que no pueden realizar el trabajo
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <ul className="divide-y divide-gray-200">
              {cotizacionesRechazadas.map((cotizacion) => (
                <li 
                  key={cotizacion.id} 
                  className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center">
                    <XCircle className="h-6 w-6 text-red-500 mr-4 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">{cotizacion.nombretutor}</p>
                      <p 
                        className="text-sm text-gray-600 truncate" 
                        title={cotizacion.comentariocotizacion}
                      >
                        Motivo: {cotizacion.comentariocotizacion || 'No especificado'}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm text-gray-500 text-right whitespace-nowrap">
                    {formatearFechaCompleta(cotizacion.fechaconfirmacion)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

    </div>
  );
  
};

export default Cotizaciones;